import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService, departmentService, designationService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
    KeyRound,
    ShieldCheck,
    ShieldAlert,
    Loader2,
    Lock,
    Building2,
    Briefcase,
    Plus,
    Edit,
    Trash2,
} from 'lucide-react';

export default function SettingsPage() {
    const { user, isAdmin } = useAuth();

    // Password change state
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });

    // Department management state
    const [departments, setDepartments] = useState([]);
    const [deptLoading, setDeptLoading] = useState(false);
    const [showDeptDialog, setShowDeptDialog] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [deptForm, setDeptForm] = useState({ name: '', description: '' });
    const [savingDept, setSavingDept] = useState(false);

    // Designation management state
    const [designations, setDesignations] = useState([]);
    const [desigLoading, setDesigLoading] = useState(false);
    const [showDesigDialog, setShowDesigDialog] = useState(false);
    const [editingDesig, setEditingDesig] = useState(null);
    const [desigForm, setDesigForm] = useState({ name: '', description: '' });
    const [savingDesig, setSavingDesig] = useState(false);

    const validatePassword = (password) => ({
        minLength: password.length >= 8,
        hasUpper: /[A-Z]/.test(password),
        hasLower: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
    });

    const checks = validatePassword(formData.new_password);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (formData.new_password !== formData.confirm_password) {
            toast.error('New passwords do not match');
            return;
        }
        if (!Object.values(checks).every(Boolean)) {
            toast.error('Password does not meet requirements');
            return;
        }
        setLoading(true);
        try {
            await authService.changePassword({
                current_password: formData.current_password,
                new_password: formData.new_password,
            });
            toast.success('Password changed successfully. Please log in again.');
            setTimeout(() => {
                localStorage.removeItem('hrms_token');
                localStorage.removeItem('hrms_user');
                window.location.href = '/login';
            }, 2000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const loadDepartments = async () => {
        setDeptLoading(true);
        try {
            const data = await departmentService.list();
            setDepartments(data);
        } catch {
            toast.error('Failed to load departments');
        } finally {
            setDeptLoading(false);
        }
    };

    const loadDesignations = async () => {
        setDesigLoading(true);
        try {
            const data = await designationService.list();
            setDesignations(data);
        } catch {
            toast.error('Failed to load designations');
        } finally {
            setDesigLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin()) {
            loadDepartments();
            loadDesignations();
        }
    }, []);

    const openAddDesig = () => {
        setEditingDesig(null);
        setDesigForm({ name: '', description: '' });
        setShowDesigDialog(true);
    };

    const openEditDesig = (desig) => {
        setEditingDesig(desig);
        setDesigForm({ name: desig.name, description: desig.description || '' });
        setShowDesigDialog(true);
    };

    const handleSaveDesig = async () => {
        if (!desigForm.name.trim()) {
            toast.error('Designation name is required');
            return;
        }
        setSavingDesig(true);
        try {
            if (editingDesig) {
                await designationService.update(editingDesig.id, desigForm);
                toast.success('Designation updated');
            } else {
                await designationService.create(desigForm);
                toast.success('Designation created');
            }
            setShowDesigDialog(false);
            loadDesignations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save designation');
        } finally {
            setSavingDesig(false);
        }
    };

    const handleDeleteDesig = async (desig) => {
        if (!window.confirm(`Delete designation "${desig.name}"?`)) return;
        try {
            await designationService.delete(desig.id);
            toast.success('Designation deleted');
            loadDesignations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete designation');
        }
    };

    const openAddDept = () => {
        setEditingDept(null);
        setDeptForm({ name: '', description: '' });
        setShowDeptDialog(true);
    };

    const openEditDept = (dept) => {
        setEditingDept(dept);
        setDeptForm({ name: dept.name, description: dept.description || '' });
        setShowDeptDialog(true);
    };

    const handleSaveDept = async () => {
        if (!deptForm.name.trim()) {
            toast.error('Department name is required');
            return;
        }
        setSavingDept(true);
        try {
            if (editingDept) {
                await departmentService.update(editingDept.id, deptForm);
                toast.success('Department updated');
            } else {
                await departmentService.create(deptForm);
                toast.success('Department created');
            }
            setShowDeptDialog(false);
            loadDepartments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save department');
        } finally {
            setSavingDept(false);
        }
    };

    const handleDeleteDept = async (dept) => {
        if (!window.confirm(`Delete department "${dept.name}"?`)) return;
        try {
            await departmentService.delete(dept.id);
            toast.success('Department deleted');
            loadDepartments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete department');
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6 fade-in">
            <div>
                <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account security and preferences
                </p>
            </div>

            {isAdmin() && (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-primary" />
                                <CardTitle>Departments</CardTitle>
                            </div>
                            <Button size="sm" onClick={openAddDept}>
                                <Plus className="w-4 h-4 mr-1" />
                                Add
                            </Button>
                        </div>
                        <CardDescription>Manage the list of departments available in the system</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        {deptLoading ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead className="hidden sm:table-cell">Description</TableHead>
                                        <TableHead className="w-24"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {departments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                                                No departments yet. Add one to get started.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        departments.map(dept => (
                                            <TableRow key={dept.id}>
                                                <TableCell className="font-medium">{dept.name}</TableCell>
                                                <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">
                                                    {dept.description || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1">
                                                        <Button size="icon" variant="ghost" onClick={() => openEditDept(dept)}>
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteDept(dept)}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            )}

            <Card className="border-primary/10 bg-card/50 backdrop-blur-sm">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <KeyRound className="w-5 h-5 text-primary" />
                        <CardTitle>Change Password</CardTitle>
                    </div>
                    <CardDescription>
                        Update your password to keep your account secure
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="current_password">Current Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="current_password"
                                    type="password"
                                    autoComplete="current-password"
                                    className="pl-10"
                                    value={formData.current_password}
                                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="new_password">New Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="new_password"
                                    type="password"
                                    autoComplete="new-password"
                                    className="pl-10"
                                    value={formData.new_password}
                                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <RequirementItem label="8+ Characters" satisfied={checks.minLength} />
                                <RequirementItem label="Uppercase" satisfied={checks.hasUpper} />
                                <RequirementItem label="Lowercase" satisfied={checks.hasLower} />
                                <RequirementItem label="Number" satisfied={checks.hasNumber} />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm_password">Confirm New Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="confirm_password"
                                    type="password"
                                    autoComplete="new-password"
                                    className="pl-10"
                                    value={formData.confirm_password}
                                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full mt-4"
                            disabled={loading || !Object.values(checks).every(Boolean)}
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Update Password
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card className="border-yellow-500/20 bg-yellow-500/5">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-yellow-500" />
                        <CardTitle className="text-yellow-500">Security Note</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-yellow-500/80">
                        Changing your password will invalidate all your current active sessions.
                        You will need to log in again on all your devices.
                    </p>
                </CardContent>
            </Card>

            {isAdmin() && (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Briefcase className="w-5 h-5 text-primary" />
                                <CardTitle>Designations</CardTitle>
                            </div>
                            <Button size="sm" onClick={openAddDesig}>
                                <Plus className="w-4 h-4 mr-1" />
                                Add
                            </Button>
                        </div>
                        <CardDescription>Manage the list of designations available in the system</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        {desigLoading ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead className="hidden sm:table-cell">Description</TableHead>
                                        <TableHead className="w-24"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {designations.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                                                No designations yet. Add one to get started.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        designations.map(desig => (
                                            <TableRow key={desig.id}>
                                                <TableCell className="font-medium">{desig.name}</TableCell>
                                                <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">
                                                    {desig.description || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1">
                                                        <Button size="icon" variant="ghost" onClick={() => openEditDesig(desig)}>
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteDesig(desig)}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Department Add/Edit Dialog */}
            <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{editingDept ? 'Edit Department' : 'Add Department'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input
                                value={deptForm.name}
                                onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                                placeholder="e.g. Engineering"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={deptForm.description}
                                onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                                placeholder="Optional description"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeptDialog(false)}>Cancel</Button>
                        <Button onClick={handleSaveDept} disabled={savingDept}>
                            {savingDept && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingDept ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Designation Add/Edit Dialog */}
            <Dialog open={showDesigDialog} onOpenChange={setShowDesigDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{editingDesig ? 'Edit Designation' : 'Add Designation'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input
                                value={desigForm.name}
                                onChange={(e) => setDesigForm({ ...desigForm, name: e.target.value })}
                                placeholder="e.g. Software Engineer"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={desigForm.description}
                                onChange={(e) => setDesigForm({ ...desigForm, description: e.target.value })}
                                placeholder="Optional description"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDesigDialog(false)}>Cancel</Button>
                        <Button onClick={handleSaveDesig} disabled={savingDesig}>
                            {savingDesig && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingDesig ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const RequirementItem = ({ label, satisfied }) => (
    <div className={`flex items-center gap-2 text-xs ${satisfied ? 'text-green-500' : 'text-muted-foreground'}`}>
        {satisfied ? <ShieldCheck className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full border border-current" />}
        {label}
    </div>
);
